/**
 * Parse service_provided text into array of service names.
 * Format: "* 美白牙齒 Teeth Whitening\n* 杜牙根 (根管治療) Root Canal Treatment"
 */
export function parseServicesFromServiceProvided(text) {
  if (!text || typeof text !== 'string') return [];
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(/^[\s*\-•]+/, '').trim())
    .filter(Boolean);
}

/**
 * Build service -> doctors mapping from clinic doctors.
 * Returns Map<serviceName, doctor[]> - doctors are full objects with meta_data
 * Services are collected from ALL doctors (including type "clinic") so the service list is complete.
 * Only non-clinic doctors are added to each service's doctor list. If only clinic has a service,
 * that service appears with empty doctor list -> use "no doctors" format.
 */
export function buildServiceToDoctorsMap(doctors) {
  const map = new Map();
  const isClinic = (d) => (d.type || '').toLowerCase() === 'clinic';
  for (const doctor of doctors || []) {
    const services = parseServicesFromServiceProvided(doctor.service_provided);
    for (const service of services) {
      const normalized = service.trim().replace(/\s+/g, ' ');
      if (!normalized) continue;
      const list = map.get(normalized) || [];
      if (!isClinic(doctor)) {
        if (!list.some((d) => d.id === doctor.id)) {
          list.push(doctor);
        }
      }
      map.set(normalized, list);
    }
  }
  return map;
}

export const DEFAULT_FORMAT_WITH_DOCTORS = `服務：{service_name}

請選擇醫生：
0. 無偏好
{doctor_list}`;

export const DEFAULT_FORMAT_NO_DOCTORS = `服務：{service_name}

**該服務並無醫生選項。**`;

export const DEFAULT_DOCTOR_LANGUAGES = {
  primary_name: true,
  zh_HK: true,
  en_US: true,
  zh_CN: false,
};

function getDoctorI18nNames(doctor) {
  const md = doctor?.meta_data;
  const i18n = md?.i18n?.name || {};
  const primaryName = (doctor?.primary_name || '').trim();
  const zhHK = (i18n.zh_HK || primaryName || '').trim();
  const en = (i18n.en_US || '').trim();
  const zhCN = (i18n.zh_CN || '').trim();
  return { primaryName, zhHK, en, zhCN };
}

/**
 * Format a single doctor using selected languages. Order: primary_name, zh_HK, en_US, zh_CN.
 * Only includes ticked languages. Skips duplicates (already shown value).
 */
export function formatDoctorDisplay(doctor, languageTicks) {
  const ticks = languageTicks || DEFAULT_DOCTOR_LANGUAGES;
  const { primaryName, zhHK, en, zhCN } = getDoctorI18nNames(doctor);
  const parts = [];
  const seen = new Set();

  const addIf = (val, key) => {
    if (!ticks[key] || !val) return;
    const v = val.trim();
    if (!v || seen.has(v)) return;
    seen.add(v);
    parts.push(v);
  };

  addIf(primaryName, 'primary_name');
  addIf(zhHK, 'zh_HK');
  addIf(en, 'en_US');
  addIf(zhCN, 'zh_CN');

  const result = parts.join(' ');
  return result || primaryName || zhHK || doctor?.primary_name || '';
}

/**
 * Generate QA answer for a service using configurable templates.
 * Placeholders: {service_name}, {doctor_list} (for with-doctors template)
 * Doctor list uses languageTicks to format each doctor (order: primary_name, zh_HK, en_US, zh_CN).
 */
export function formatQAAnswer(
  serviceName,
  doctors,
  formatWithDoctors,
  formatNoDoctors,
  languageTicks
) {
  const templateWith = formatWithDoctors || DEFAULT_FORMAT_WITH_DOCTORS;
  const templateNo = formatNoDoctors || DEFAULT_FORMAT_NO_DOCTORS;

  if (!doctors || doctors.length === 0) {
    return templateNo.replace(/\{service_name\}/g, serviceName);
  }

  const doctorList = doctors
    .map((d, i) => `${i + 1}. ${formatDoctorDisplay(d, languageTicks)}`)
    .join('\n');
  return templateWith
    .replace(/\{service_name\}/g, serviceName)
    .replace(/\{doctor_list\}/g, doctorList);
}
